import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The rail's collapse control, and its twin that expands it again (Brand Kit § The frame).
 *
 * TWO BUTTONS, ONE AT EACH END OF THE RAIL, and `shell.css` shows at most one. Collapse sits on the
 * brand row of the expanded rail; expand sits above the account in the collapsed one, where the
 * proposal board puts it. One element moved between the two places by CSS would keep its DOM
 * position and put its focus stop at the wrong end of the rail, so the order a keyboard meets the
 * control in would not be the order the eye does.
 *
 * Because the button pressed is the one that disappears, `AppRail` moves focus to its twin after
 * the toggle; otherwise focus would fall back to the document body.
 *
 * The name says what happens ("Riduci il menu"), not what the state is, so no `aria-expanded`: the
 * rail's entries stay reachable and named in both states, and nothing is hidden from a screen
 * reader by collapsing it.
 */
export function RailToggle({
  collapsed,
  onToggle,
  className,
  ref,
}: {
  /** The state the rail is in. The button offers the other one. */
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
}) {
  const { t } = useTranslation('shell');
  const label = collapsed ? t('rail.expand') : t('rail.collapse');

  return (
    <button
      ref={ref}
      type="button"
      className={className === undefined ? 'rail-toggle' : `rail-toggle ${className}`}
      aria-label={label}
      title={label}
      onClick={onToggle}
    >
      {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
    </button>
  );
}
